import type { FlagDefinition, FlagValueOf, JsonFlagValue } from '@ringba/config/flags';
import { Event, type IEvent } from '@zajno/common/observing/event';
import type { IStorageSync } from '@zajno/common/storage/types';
import { DelegatingFlagsClient } from './delegatingFlagsClient';
import type { ISystemFlagsClient } from './types';

/** The storage key the override layer keeps its overrides under. */
export const OVERRIDES_STORAGE_KEY = 'ringba.systemFlags.overrides';

export interface SystemFlagsOverridesWindowHook {
  set(key: string, value: JsonFlagValue): void;
  /** Clears one override, or every override when `key` is omitted. */
  clear(key?: string): void;
  list(): Record<string, JsonFlagValue>;
}

// `declare global` is pure type augmentation with no runtime output.
// Its `TSModuleDeclaration` AST node is what enums and namespaces also produce, which the erasable-syntax rule can't distinguish.
// eslint-disable-next-line no-restricted-syntax
declare global {
  interface Window {
    /** Installed by {@link withOverrides} while its client is alive. */
    __systemFlags?: SystemFlagsOverridesWindowHook;
  }
}

/**
 * Wraps `client` so an override set through `window.__systemFlags` takes precedence over its values.
 *
 * Overrides:
 * - Load from `storage` once, when wrapping; without `storage` they last until the page unloads.
 * - Setting or clearing one writes through to `storage` and fires `onChanged`, as does any notification from `client`.
 * - A value whose type does not match the flag's definition is ignored.
 *
 * Disposing the result uninstalls the hook, then disposes `client`.
 */
export function withOverrides(client: ISystemFlagsClient, storage?: IStorageSync<string>): ISystemFlagsClient {
  return new OverridingFlagsClient(client, storage);
}

class OverridingFlagsClient extends DelegatingFlagsClient {
  private readonly _onChanged = new Event<void>();
  private readonly _storage: IStorageSync<string> | undefined;
  private readonly _values: Map<string, JsonFlagValue>;
  private readonly _hook: SystemFlagsOverridesWindowHook;
  private readonly _unsubscribe: () => void;

  constructor(client: ISystemFlagsClient, storage: IStorageSync<string> | undefined) {
    super(client);
    this._storage = storage;
    this._values = new Map(Object.entries(readStoredOverrides(storage)));
    this._unsubscribe = client.onChanged.on(() => this._onChanged.trigger());
    this._hook = {
      set: (key, value) => this.set(key, value),
      clear: (key) => this.clear(key),
      list: () => Object.fromEntries(this._values),
    };
    window.__systemFlags = this._hook;
  }

  get onChanged(): IEvent<void> {
    return this._onChanged.expose();
  }

  get<F extends FlagDefinition>(flag: F): FlagValueOf<F> {
    const value = this._values.get(flag.key);
    return value !== undefined && matchesFlagType(value, flag) ? value as FlagValueOf<F> : super.get(flag);
  }

  dispose(): void {
    this._unsubscribe();
    // A wrapper created later may have installed its own hook since.
    if (window.__systemFlags === this._hook) {
      window.__systemFlags = undefined;
    }
    this._onChanged.resetHandlers();
    super.dispose();
  }

  private set(key: string, value: JsonFlagValue): void {
    this._values.set(key, value);
    this.commit();
  }

  private clear(key?: string): void {
    if (key === undefined) {
      this._values.clear();
    } else {
      this._values.delete(key);
    }
    this.commit();
  }

  /** Writes the overrides through to storage, then notifies. */
  private commit(): void {
    try {
      this._storage?.setValue(OVERRIDES_STORAGE_KEY, JSON.stringify(Object.fromEntries(this._values)));
    } catch {
      // Storage access blocked — the override still applies in memory.
    }
    this._onChanged.trigger();
  }
}

/** True when `value` has the type `flag` declares. */
function matchesFlagType(value: JsonFlagValue, flag: FlagDefinition): boolean {
  switch (flag.type) {
    case 'boolean':
      return typeof value === 'boolean';
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number';
    case 'object':
      // `ObjectFlagDefinition` permits an array default, so only `Array.isArray` agreement is checked — the definition carries no deeper shape.
      return value !== null
        && typeof value === 'object'
        && Array.isArray(value) === Array.isArray(flag.defaultValue);
  }
}

function readStoredOverrides(storage: IStorageSync<string> | undefined): Record<string, JsonFlagValue> {
  if (!storage) {
    return {};
  }

  try {
    const raw = storage.getValue(OVERRIDES_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, JsonFlagValue>;
    }
  } catch {
    // Malformed stored value, or storage access blocked — ignored, never thrown.
  }
  return {};
}
